import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import loadable from '@loadable/component';

import css from './FooterContainer.module.css';
import { IconCollection } from '../../components';

const SectionBuilder = loadable(
  () => import(/* webpackChunkName: "SectionBuilder" */ '../PageBuilder/PageBuilder'),
  {
    resolveComponent: components => components.SectionBuilder,
  }
);

const COOKIE_HIDDEN_KEY = 'manage-cookies-icon-hidden';

const FooterComponent = () => {
  const { footer = {}, topbar } = useConfiguration();
  const intl = useIntl();
  const [isCookieIconHidden, setIsCookieIconHidden] = useState(true); // Default to true to hide during SSR/initial load flicker

  useEffect(() => {
    // Check if cookie exists on mount
    try {
      const isHidden = Cookies.get(COOKIE_HIDDEN_KEY) === 'true';
      setIsCookieIconHidden(isHidden);
    } catch (e) {
      console.error('Failed to read cookie icon hidden state', e);
      setIsCookieIconHidden(false);
    }
  }, []);

  const cookieSettingsLabel = intl.formatMessage({ id: 'FooterContainer.cookieSettings' });
  const closeCookieSettingsLabel = intl.formatMessage({ id: 'FooterContainer.closeCookieSettings' });

  // If footer asset is not set, let's not render Footer at all.
  if (Object.keys(footer).length === 0) {
    return null;
  }

  // The footer asset does not specify sectionId or sectionType. However, the SectionBuilder
  // expects sectionId and sectionType in order to identify the section. We add those
  // attributes here before passing the asset to SectionBuilder.
  const footerSection = {
    ...footer,
    sectionId: 'footer',
    sectionType: 'footer',
    linkLogoToExternalSite: topbar?.logoLink,
  };

  const handleOpenCookieSettings = () => {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new Event('cookieconsent:openPreferences'));
    } else {
      console.error('Cookie settings are unavailable because the browser event API is missing.');
    }
  };

  const handleHideCookieIcon = e => {
    e.stopPropagation(); // Avoid opening the settings modal when clicking the close icon
    try {
      // Set cookie to persist hidden state for 1 year
      Cookies.set(COOKIE_HIDDEN_KEY, 'true', { expires: 365, path: '/', sameSite: 'Lax' });
      setIsCookieIconHidden(true);
    } catch (e) {
      console.error('Failed to save cookie icon hidden state', e);
    }
  };

  return (
    <>
      <SectionBuilder sections={[footerSection]} />
      {!isCookieIconHidden ? (
        <div className={css.cookieSettingsWrapper}>
          <button
            type="button"
            className={css.cookieSettingsButton}
            onClick={handleOpenCookieSettings}
            aria-label={cookieSettingsLabel}
            title={cookieSettingsLabel}
          >
            <IconCollection iconName="cookies" />
          </button>
          <button
            type="button"
            className={css.closeIcon}
            onClick={handleHideCookieIcon}
            aria-label={closeCookieSettingsLabel}
            title={closeCookieSettingsLabel}
          >
            ✕
          </button>
        </div>
      ) : null}
    </>
  );
};

// NOTE: if you want to add dynamic data to FooterComponent,
//       you could just connect this FooterContainer to Redux Store
//
// const mapStateToProps = state => {
//   const { currentUser } = state.user;
//   return { currentUser };
// };
// const FooterContainer = compose(connect(mapStateToProps))(FooterComponent);
// export default FooterContainer;

export default FooterComponent;
